import { useQuery } from "@tanstack/react-query";

export interface PublicHoliday {
  date: string; // YYYY-MM-DD
  localName: string;
  name: string;
  countryCode: string;
  global: boolean;
  counties: string[] | null;
}

async function fetchHolidaysForYear(year: number): Promise<PublicHoliday[]> {
  try {
    const response = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/AU`
    );
    if (!response.ok) return [];
    return response.json();
  } catch {
    return [];
  }
}

export function usePublicHolidays(year: number) {
  return useQuery<PublicHoliday[]>({
    queryKey: ["publicHolidays", year],
    queryFn: () => fetchHolidaysForYear(year),
    staleTime: 1000 * 60 * 60 * 24, // Cache 24 hours
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function usePublicHolidaysForYears(years: number[]) {
  // Deduplicate years
  const uniqueYears = [...new Set(years)];
  return useQuery<PublicHoliday[]>({
    queryKey: ["publicHolidays", ...uniqueYears],
    queryFn: async () => {
      const results = await Promise.all(uniqueYears.map(fetchHolidaysForYear));
      return results.flat();
    },
    staleTime: 1000 * 60 * 60 * 24,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function getHolidayForDate(
  holidays: PublicHoliday[],
  date: Date
): PublicHoliday | undefined {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateStr = `${year}-${month}-${day}`;
  return holidays.find((h) => h.date === dateStr);
}
